import { connectionHealth, GOOD, isWorse, WORSEN_AFTER_MS, type ConnectionHealth, type HealthLevel } from '@novachat/client';
import { useEffect, useLayoutEffect, useState } from 'react';
import { create } from 'zustand';
import { useLive } from './live';
import { useVoice } from './voice';

/** Your connection as the call bar and the connection card show it: a worse state only once it lasts, a better one at once. */
export function useConnectionHealth(): ConnectionHealth {
  const realtime = useLive((s) => s.status);
  const call = useVoice((s) => (s.room ? s.connection : 'idle'));
  const quality = useVoice((s) => s.quality);
  const now = connectionHealth({ realtime, call, quality });
  const [shown, setShown] = useState<ConnectionHealth>(GOOD);
  useEffect(() => {
    if (!isWorse(now.level, shown.level)) {
      if (now.level !== shown.level || now.text !== shown.text) setShown(now);
      return;
    }
    const timer = setTimeout(() => setShown(now), WORSEN_AFTER_MS);
    return () => clearTimeout(timer);
    // Only a change in what to show restarts the wait; `shown` is read as it was then.
  }, [now.level, now.text]);
  return shown;
}

/** The call bar's flow in each state: green, yellow, red (styles.css). */
export const FLOW: Record<HealthLevel, string> = { good: 'nc-call-flow', fair: 'nc-call-flow nc-flow-fair', poor: 'nc-call-flow nc-flow-poor' };

/** How many call bars are on screen: the connection card stays away while a bar tells the same. */
export const useShownBars = create<{ count: number }>(() => ({ count: 0 }));

/** Put inside a bar while it shows (before paint, so the card never flashes next to it). */
export function ShownBar() {
  useLayoutEffect(() => {
    useShownBars.setState((s) => ({ count: s.count + 1 }));
    return () => useShownBars.setState((s) => ({ count: s.count - 1 }));
  }, []);
  return null;
}
